import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { BaseInfoCompProps, capitalizeAll, capitalizeFirst, SelectInputOption } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, setIsEditingForm, showErrorNotification } from '@moderepo/mode-ui-state';
import { userAppDataContext } from '@moderepo/mode-data-state-user-app';
import produce, { Draft } from 'immer';
import { TFunction } from 'i18next';
import { Agent, Entity, EntityMetricsDefinition, SortOrder } from '@moderepo/mode-internal-apis';
import { getDataIntervalViewTypeName, getDataSourceSelectedMetricNames, initializeTSDBDashboardWidgetDataSourceUserInputs,
    initializeDataDashboardWidgetUserInputs, initializeDashboardWidgetBaseViewUserInputs, useCreateTSDBDashboardWidgetSettingsCompFields,
    createViewMetricMetadataInputField, getUpdateDashboardWidgetDataSourceInputs, createDataSourceSettingsFromInputs,
    getTSDashboardWidgetSettingsCompTexts,
    useWidgetDataSource,
    getSortOrderName } from './dashboardUtils';
import { DataIntervalDashboardWidgetInputErrors, DataIntervalDashboardWidgetSettings, DashboardWidgetType, DataIntervalDashboardWidgetInputs,
    DataIntervalViewType, DASHBOARD_WIDGET_MIN_WIDTH, DASHBOARD_WIDGET_MIN_HEIGHT, WIDGET_GROUP_WIDGET_MIN_HEIGHT, TSDBDataSourceType,
    isEntityDashboardWidgetDataSource,
    AggregatedDataIntervalType,
    RawDataIntervalType } from './dashboardTypes';
import { DataIntervalDashboardWidgetSettingsComp, DataIntervalDashboardWidgetSettingsCompProps }
    from '../../components/dashboard/DataIntervalDashboardWidgetSettingsComp';
import { useRitzTranslation } from '../../utils/customHooks';
import { createNewHomeDashboardWidget, createNewProjectDashboardWidget, DashboardLogicApiCallResponse, updateHomeDashboardWidget,
    updateProjectDashboardWidget, validateDataIntervalDashboardWidgetInputs } from './dashboardLogic';
import { TSDBDashboardWidgetDataSourceInputsCompProps } from '../../components';
import { useSelectEntity } from '../../utils/dataStateHooks';
import { BaseDashboardWidgetSettingsCompFields, DataDashboardWidgetSettingsCompFields, TSDBDashboardWidgetSettingsFields }
    from '../../components/dashboard/dashboardCompUtils';
import { isEntitiesFeatureEnabled } from '../../utils/utils';
import { useSelectTranslatedEntityClasses } from '..';


const useStyle = makeStyles(() => {
    return {
        widget: {
            width: '100%',
        },
    };
});



/**
 * Create an Array of View Type options to be used for the View Type SELECT input fields. These are the 'Line Chart', 'Bar Chart', 'Tabular' options
 */
const createViewTypeOptions = (
    trans: TFunction, inputs: DataIntervalDashboardWidgetInputs,
): readonly SelectInputOption<DataIntervalViewType> [] => {
    return Object.values(DataIntervalViewType).map((type): SelectInputOption<DataIntervalViewType> => {
        const { dataSourceType } = inputs;
        const isCollectionOrEntityDS = dataSourceType === TSDBDataSourceType.COLLECTION || dataSourceType === TSDBDataSourceType.ENTITY;
        const isSimpleSeriesDataSource = dataSourceType === TSDBDataSourceType.SIMPLE;

        // XYCharts can only be used with SIMPLE time series, Collection, and Entity 's numeric metrics (valueNames)
        // GANTT_CHART can only be used with Collection and Entity's tagMetrics, tagNames).
        const disabled = (type === DataIntervalViewType.LINE_CHART && isCollectionOrEntityDS && inputs.tagNames && inputs.tagNames.length > 0)
            || (type === DataIntervalViewType.BAR_CHART && isCollectionOrEntityDS && inputs.tagNames && inputs.tagNames.length > 0)
            || (type === DataIntervalViewType.AREA_CHART && isCollectionOrEntityDS && inputs.tagNames && inputs.tagNames.length > 0)
            || (type === DataIntervalViewType.GANTT_CHART && (isSimpleSeriesDataSource || (inputs.valueNames && inputs.valueNames.length > 0)));

        return {
            value: type,
            label: capitalizeFirst(trans(getDataIntervalViewTypeName(type))),
            disabled,
        };
    });
};


/**
 * Create an Array of Sort Order options to be used for the Sort Order SELECT input fields. These are the 'Asc', 'Desc' options
 */
const createSortOrderOptions = (
    trans: TFunction,
): readonly SelectInputOption<SortOrder> [] => {
    return [{
        value: SortOrder.ASC,
        label: capitalizeFirst(trans(getSortOrderName(SortOrder.ASC))),
    }, {
        value: SortOrder.DESC,
        label: capitalizeFirst(trans(getSortOrderName(SortOrder.DESC))),
    }];
};


const initializeUserInputs = (
    widget?: DataIntervalDashboardWidgetSettings | undefined,
    entityFeatureEnabled?: boolean,
    entity?: Entity | undefined,
    metricsDefinition?: EntityMetricsDefinition | undefined,
    homeId?: number | undefined,
): DataIntervalDashboardWidgetInputs => {

    return {
        ...initializeDataDashboardWidgetUserInputs(widget),
        ...initializeTSDBDashboardWidgetDataSourceUserInputs(widget?.dataSource, entityFeatureEnabled, entity, metricsDefinition, homeId),
        ...initializeDashboardWidgetBaseViewUserInputs(widget?.viewSettings.metricsMetadata, getDataSourceSelectedMetricNames(widget?.dataSource)),

        // Initialize other inputs for the DataIntervalDashboardWidget
        viewType   : widget?.viewSettings.viewType ?? DataIntervalViewType.LINE_CHART,
        showBullets: widget?.viewSettings.showBullets ?? false,
        showLegends: widget?.viewSettings.showLegends ?? false,
        showYAxes  : widget?.viewSettings.showYAxes ?? true,
        sortOrder  : widget?.viewSettings.sortOrder ?? SortOrder.ASC,
    };

};


export interface DataIntervalDashboardWidgetSettingsContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly dashboardId: number;
    readonly selectedWidget?: DataIntervalDashboardWidgetSettings | undefined;

    // The ID of the widget, type WidgetGroup, to place the new widget into. This is OPTIONAL and only for creating new widget.
    readonly widgetGroupId?: number | undefined;

    // Only available if this container is used for creating new widget
    readonly newWidgetHandler?: {
        readonly onNewWidgetCreated: ()=> void;
        readonly onNewWidgetCanceled: ()=> void;
    } | undefined;

    // This is a container that wraps a DUMB component and some time we need to pass some props such as className, styles, etc... into the
    // component from the container of this container so this is a way for us to pass props into the component
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;


    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly homeId?: number | undefined;
    }
}



/**
 * This is a wrapper for the DataIntervalDashboardWidget. It contains all the logic for the widget.
 */
export const DataIntervalDashboardWidgetSettingsContainer: React.FC<DataIntervalDashboardWidgetSettingsContainerProps> = (
    props: DataIntervalDashboardWidgetSettingsContainerProps,
) => {

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const { trans } = useRitzTranslation(project);

    // For optimization, use the 'useWidgetDataSource' hook to get the dataSource. This hook will make sure this object
    // doesn't change unless the content of the dataSource changed.
    const dataSource = useWidgetDataSource(props.selectedWidget?.dataSource);

    const entityFeatureEnabled = isEntitiesFeatureEnabled(project, loggedInAgent);

    // Get all the entity classes
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // Get the Entity object for the data source if the data source is an Entity data source
    const selectedEntity = useSelectEntity(
        props.projectId,
        isEntityDashboardWidgetDataSource(dataSource) ? dataSource.entityId : undefined,
        false,
    );

    // Get the metricsDefinition for the selectedEntity if the data source is an Entity
    const selectedMetricsDefinition = useMemo(() => {
        if (isEntityDashboardWidgetDataSource(dataSource) && dataSource.metricsDefinitionId && entityClasses && selectedEntity) {
            // Get the EntityClass object based on the selectedEntity.entityClass
            const entityClass = entityClasses.find(((entClass) => {
                return entClass.entityClass === selectedEntity.entityClass;
            }));

            if (entityClass) {
                return entityClass.metricsDefinitions?.find((definition) => {
                    return definition.metricsDefinitionId === dataSource.metricsDefinitionId;
                });
            }
        }
        return undefined;
    }, [dataSource, entityClasses, selectedEntity]);


    // A cached values of the user inputs. Usually we don't need to cache the user inputs but we do need it in this case because we need to load
    // additional data based on the user inputs
    const [currentUserInputs, setCurrentUserInputs] = useState<DataIntervalDashboardWidgetInputs>(
        initializeUserInputs(props.selectedWidget, entityFeatureEnabled, selectedEntity, selectedMetricsDefinition, props.routeContext?.homeId),
    );

    const classes = useStyle();
    const isNewWidget = props.selectedWidget === undefined;
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputErrors, setInputErrors] = useState<DataIntervalDashboardWidgetInputErrors>({ });


    /**
     * This useEffect is used for re-initializing the currentUserInputs when props.selectedWidget changed. useState WIll NOT re-initialize
     * the currentUserInputs therefore we need useEffect to do it. If we don't re-initialize the inputs, the settings form will stay the same
     * even when the user selected a different widget.
     */
    useEffect(() => {
        setCurrentUserInputs(initializeUserInputs(
            props.selectedWidget, entityFeatureEnabled, selectedEntity, selectedMetricsDefinition, props.routeContext?.homeId,
        ));
    }, [entityFeatureEnabled, props.routeContext, props.selectedWidget, selectedEntity, selectedMetricsDefinition]);



    /**
     * Drop down options to be used in the View Type input field
     */
    const viewTypeOptions = useMemo((): readonly SelectInputOption<DataIntervalViewType> [] => {
        return createViewTypeOptions(trans, currentUserInputs);
    }, [currentUserInputs, trans]);


    /**
     * Drop down options to be used in the View Type input field
     */
    const sortOptions = useMemo((): readonly SelectInputOption<SortOrder> [] => {
        return createSortOrderOptions(trans);
    }, [trans]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.widgetId is undefined which is when user want to create a Widget
     */
    useEffect(() => {
        if (isNewWidget) {
            onChangeEditingMode(true);
        }
    }, [isNewWidget, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
        });

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (props.newWidgetHandler !== undefined) {
            props.newWidgetHandler.onNewWidgetCanceled();
        }

        // Reset the cached user input with the selectedWidget's data
        setCurrentUserInputs(initializeUserInputs(
            props.selectedWidget, entityFeatureEnabled, selectedEntity, selectedMetricsDefinition, props.routeContext?.homeId,
        ));
    }, [closeEditing, entityFeatureEnabled, props.newWidgetHandler, props.routeContext, props.selectedWidget, selectedEntity,
        selectedMetricsDefinition]);



    /**
     * Create a new widget
     */
    const createNewWidget = useCallback(async (inputs: DataIntervalDashboardWidgetInputs) => {

        // Validate inputs and get the set of errors
        const newErrors = validateDataIntervalDashboardWidgetInputs(inputs, trans);
        setInputErrors(newErrors);

        // Go through all the values in the newErrors object to find a non undefined value. If there is at least 1 non undefined value,
        // that mean there is an error.
        const hasErrors = Object.values(newErrors).some((error) => {
            return error !== undefined;
        });


        if (!hasErrors && inputs.title && inputs.reloadDataFreq !== undefined && inputs.viewType) {

            // Create a new widget using the provided input and add it to the Home Dashboard KV
            const newWidget: DataIntervalDashboardWidgetSettings = {
                id           : Date.now(),
                widgetGroupId: props.widgetGroupId,
                type         : DashboardWidgetType.DATA_INTERVAL,
                layouts      : {
                    lg: {
                        x: null,        // Give the new Widget null value for X/Y position since they don't have a position yet. We will not give
                        y: null,        // them value 0/0 because there might already be a widget at location 0/0.
                        w: DASHBOARD_WIDGET_MIN_WIDTH * 2,
                        h: (props.widgetGroupId ? WIDGET_GROUP_WIDGET_MIN_HEIGHT : DASHBOARD_WIDGET_MIN_HEIGHT) * 2,
                    },
                },
                title               : inputs.title,
                icon                : inputs.icon,
                reloadDataFreqInMins: inputs.reloadDataFreq,
                dataSource          : createDataSourceSettingsFromInputs(inputs),   // call helper function to create dataSource object from inputs
                viewSettings        : {
                    viewType       : inputs.viewType,
                    showBullets    : inputs.showBullets,
                    showLegends    : inputs.showLegends,
                    showYAxes      : inputs.showYAxes,
                    sortOrder      : inputs.sortOrder,
                    metricsMetadata: inputs.viewMetricsMetadata,
                },
            };

            const onCompleted = (result: DashboardLogicApiCallResponse) => {
                if (result.success) {
                    // Dashboard created successfully
                    closeEditing();

                    // Notify the handler a new widget is added
                    if (props.newWidgetHandler !== undefined) {
                        props.newWidgetHandler.onNewWidgetCreated();
                    }
                } else if (result.errorMessageKey) {
                    uiDispatch(showErrorNotification(trans(result.errorMessageKey)));
                }
            };

            if (props.routeContext?.homeId) {
                createNewHomeDashboardWidget(appDataState, appDataDispatch, props.routeContext.homeId, props.dashboardId, newWidget)
                    .then(onCompleted);
            } else {
                createNewProjectDashboardWidget(dataState, dataDispatch, props.projectId, loggedInAgent.id, props.dashboardId, newWidget)
                    .then(onCompleted);
            }
        }

    }, [props.widgetGroupId, props.routeContext, props.newWidgetHandler, props.dashboardId, props.projectId, closeEditing, uiDispatch, trans,
        appDataState, appDataDispatch, dataState, dataDispatch, loggedInAgent.id]);



    /**
     * Updating an existing widget
     */
    const updateWidget = useCallback(async (inputs: DataIntervalDashboardWidgetInputs, widget: DataIntervalDashboardWidgetSettings) => {

        // Validate inputs and get the set of errors
        const newErrors = validateDataIntervalDashboardWidgetInputs(inputs, trans);
        setInputErrors(newErrors);

        // Go through all the values in the newErrors object to find a non undefined value. If there is at least 1 non undefined value,
        // that mean there is an error.
        const hasErrors = Object.values(newErrors).some((error) => {
            return error !== undefined;
        });

        // If there is no error, make an API call to update the widget
        if (!hasErrors && inputs.title) {

            // Make a copy of the current 'widget' and replace the editable props with the values from user inputs
            const updatedWidget: DataIntervalDashboardWidgetSettings = {
                ...widget,
                type                : DashboardWidgetType.DATA_INTERVAL,
                title               : inputs.title ?? widget.title,
                icon                : inputs.icon,
                reloadDataFreqInMins: inputs.reloadDataFreq ?? widget.reloadDataFreqInMins,
                dataSource          : createDataSourceSettingsFromInputs(inputs),   // call helper function to create dataSource object from inputs
                viewSettings        : {
                    viewType       : inputs.viewType ?? widget.viewSettings.viewType,
                    showBullets    : inputs.showBullets ?? widget.viewSettings.showBullets,
                    showLegends    : inputs.showLegends ?? widget.viewSettings.showLegends,
                    showYAxes      : inputs.showYAxes ?? widget.viewSettings.showYAxes,
                    sortOrder      : inputs.sortOrder ?? widget.viewSettings.sortOrder,
                    metricsMetadata: inputs.viewMetricsMetadata,
                },
            };


            const onCompleted = (result: DashboardLogicApiCallResponse) => {
                if (result.success) {
                    // Dashboard updated successfully
                    closeEditing();
                } else if (result.errorMessageKey) {
                    uiDispatch(showErrorNotification(trans(result.errorMessageKey)));
                }
            };

            if (props.routeContext?.homeId) {
                updateHomeDashboardWidget(appDataState, appDataDispatch, props.routeContext.homeId, props.dashboardId, updatedWidget)
                    .then(onCompleted);
            } else {
                updateProjectDashboardWidget(dataState, dataDispatch, props.projectId, loggedInAgent.id, props.dashboardId, updatedWidget)
                    .then(onCompleted);
            }
        }
    }, [appDataDispatch, appDataState, closeEditing, dataDispatch, dataState, loggedInAgent.id, props.dashboardId, props.projectId,
        props.routeContext, trans, uiDispatch]);



    /**
     * On Edit/Create widget form submit event. Update the widget or create a new widget and save the widget settings in Home Dashboard
     */
    const onEditWidgetFormSubmit = useCallback(async (inputs: DataIntervalDashboardWidgetInputs) => {
        if (props.selectedWidget === undefined) {
            createNewWidget(inputs);
        } else {
            updateWidget(inputs, props.selectedWidget);
        }
    }, [createNewWidget, props.selectedWidget, updateWidget]);



    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onEditWidgetFormInputChange = useCallback((updatedFieldName: string, newValue: any) => {

        // Convert 'name' from just any string to a more strict name from this object DataIntervalDashboardWidgetProps['fields']
        // This will make sure we don't misspell the name below when we try to check which field was updated. The compiler will throw error
        // if we try to compare filedName to some random string that is not part of DataIntervalDashboardWidgetProps['fields'].
        const fieldName = updatedFieldName as (
            keyof BaseDashboardWidgetSettingsCompFields |
            keyof DataDashboardWidgetSettingsCompFields |
            keyof TSDBDashboardWidgetSettingsFields |
            keyof DataIntervalDashboardWidgetSettingsCompProps['fields']['view']
        );


        // since the input error object use the same field name as the input field, we can do it this way
        setInputErrors(produce(inputErrors, (draft: Draft<DataIntervalDashboardWidgetInputErrors>) => {
            draft[fieldName] = undefined;
        }));


        // If the updated field is one of the following fields then we need to use a special function, getUpdateDashboardWidgetDataSourceInputs,
        // to get the updated user inputs
        const specialFields = [
            'homeId', 'smartModuleId', 'timeSeriesType', 'seriesIds', 'collectionId', 'valueNames', 'tagNames',
            'dataSourceType', 'entity', 'metricsDefinition',
        ];


        if (specialFields.includes(fieldName)) {
            setCurrentUserInputs(getUpdateDashboardWidgetDataSourceInputs(
                fieldName as keyof TSDBDashboardWidgetDataSourceInputsCompProps['fields'], newValue, currentUserInputs,
            ));
        } else {
            // For all other field, it will be a straightforward update, replace the value of the 'fieldName' field with the new value
            setCurrentUserInputs(produce(currentUserInputs, (draft: Draft<DataIntervalDashboardWidgetInputs>) => {
                // update the value of the field that changed
                draft[fieldName] = newValue;
            }));
        }
    }, [currentUserInputs, inputErrors]);


    /**
     * Use `useCreateTSDBDashboardWidgetSettingsCompFields` to help create the required input fields for TSDB dash board widget components
     */
    const tsdbDashboardWidgetSettingsCompFields = useCreateTSDBDashboardWidgetSettingsCompFields(
        props.projectId,
        props.routeContext?.homeId,
        props.selectedWidget,
        true,
        [
            ...Object.values(AggregatedDataIntervalType),
            ...Object.values(RawDataIntervalType).filter((intervalType) => {
                // We need to exclude LAST_VALUE because it is SINGLE value which is not used for DataInterval widget
                return intervalType !== RawDataIntervalType.LAST_VALUE;
            }),
        ],
        currentUserInputs,
        inputErrors,
        entityClasses,
        trans,
    );


    /**
     * Create and cache the set of fields for the dashboard widget settings
     */
    const dashboardWidgetCompFields = useMemo((): DataIntervalDashboardWidgetSettingsCompProps['fields'] => {

        // hideMetricsMetadataField if the viewType is NOT one of the Chart views. OR if the 'Show YAxes' option is UNCHECKED.
        const hideMetricsMetadataField = !currentUserInputs.viewType || ![
            DataIntervalViewType.LINE_CHART,
            DataIntervalViewType.BAR_CHART,
            DataIntervalViewType.AREA_CHART,
        ].includes(currentUserInputs.viewType) || !currentUserInputs.showYAxes;

        // Combine input fields from `tsdbDashboardWidgetSettingsCompFields` with custom input fields
        return {
            ...tsdbDashboardWidgetSettingsCompFields,
            view: {
                viewType: {
                    label         : capitalizeAll(trans('dashboard.labels.data_interval_view_type')),
                    value         : currentUserInputs.viewType,
                    options       : viewTypeOptions,
                    noOptionsLabel: capitalizeAll(trans('label.no_options')),
                    editable      : true,
                    error         : inputErrors.viewType ? trans(inputErrors.viewType) : undefined,
                },
                showBullets: {
                    label   : capitalizeAll(trans('label.show_chart_bullets')),
                    value   : currentUserInputs.showBullets,
                    editable: true,
                    hidden  : !currentUserInputs.viewType || ![
                        DataIntervalViewType.LINE_CHART,
                    ].includes(currentUserInputs.viewType),
                },
                showLegends: {
                    label   : capitalizeAll(trans('label.show_chart_legends')),
                    value   : currentUserInputs.showLegends,
                    editable: true,
                    hidden  : !currentUserInputs.viewType || ![
                        DataIntervalViewType.LINE_CHART,
                        DataIntervalViewType.BAR_CHART,
                        DataIntervalViewType.AREA_CHART,
                    ].includes(currentUserInputs.viewType),
                },
                showYAxes: {
                    label   : capitalizeAll(trans('label.show_chart_yaxes')),
                    value   : currentUserInputs.showYAxes,
                    editable: true,
                    hidden  : !currentUserInputs.viewType || ![
                        DataIntervalViewType.LINE_CHART,
                        DataIntervalViewType.BAR_CHART,
                        DataIntervalViewType.AREA_CHART,
                    ].includes(currentUserInputs.viewType),
                },
                sortOrder: {
                    label   : capitalizeAll(trans('label.sort_order')),
                    value   : currentUserInputs.sortOrder,
                    options : sortOptions,
                    editable: true,
                    hidden  : !currentUserInputs.viewType || [
                        DataIntervalViewType.LINE_CHART,
                        DataIntervalViewType.BAR_CHART,
                        DataIntervalViewType.AREA_CHART,
                    ].includes(currentUserInputs.viewType),
                },
                ...(createViewMetricMetadataInputField({
                    currentUserInputs,
                    inputErrors,
                    hidden       : hideMetricsMetadataField,
                    contextHomeId: props.routeContext?.homeId,
                    trans,
                })),
            },
        };
    }, [currentUserInputs, tsdbDashboardWidgetSettingsCompFields, trans, viewTypeOptions, inputErrors, sortOptions, props.routeContext?.homeId]);



    /**
     * Create a cache the texts for the Dashboard Widget component
     */
    const dashboardWidgetCompTexts = useMemo((): DataIntervalDashboardWidgetSettingsCompProps['texts'] => {
        return getTSDashboardWidgetSettingsCompTexts(trans);
    }, [trans]);


    /**
     * Create a cache the actions for the dashboard widget components
     */
    const dashboardWidgetCompActions = useMemo((): DataIntervalDashboardWidgetSettingsCompProps['actions'] => {
        return {
            startEdit: {
                onClick: () => {
                    onChangeEditingMode(true);
                },
            },
            cancelEdit: {
                label  : capitalizeAll(trans('label.cancel')),
                onClick: onCancelEdit,
            },
            saveUpdate: {
                label  : isNewWidget ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                onClick: onEditWidgetFormSubmit,
            },
        };
    }, [isNewWidget, onCancelEdit, onChangeEditingMode, onEditWidgetFormSubmit, trans]);



    return (
        <DataIntervalDashboardWidgetSettingsComp
            className={clsx(classes.widget, props.className, props.infoCompProps?.className, isEditing && 'editing')}
            isEditing={isEditing}
            title={capitalizeAll(trans('label.dashboard_widget_settings'))}
            icon="settings"
            formProps={props.infoCompProps?.formProps}
            fields={dashboardWidgetCompFields}
            texts={dashboardWidgetCompTexts}
            actions={dashboardWidgetCompActions}
            onFieldClicked={() => {
                onChangeEditingMode(true);
            }}
            onFormInputChange={onEditWidgetFormInputChange}
        />
    );
};
